import React from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const ClientTable = ({
  clients,
  selectedClients,
  sortConfig,
  onSort,
  onClientSelect,
  onClientAction,
  formatCurrency,
  formatDate
}) => {
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <Icon name="ArrowUpDown" size={16} className="text-text-secondary" />;
    }
    return sortConfig.direction === 'asc' 
      ? <Icon name="ArrowUp" size={16} className="text-primary" />
      : <Icon name="ArrowDown" size={16} className="text-primary" />;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-success-100', text: 'text-success-700', label: 'Active' },
      inactive: { bg: 'bg-secondary-100', text: 'text-secondary-700', label: 'Inactive' }
    };
    
    const config = statusConfig[status] || statusConfig.active;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getClientTypeBadge = (type) => {
    const typeConfig = {
      individual: { bg: 'bg-primary-100', text: 'text-primary-700', label: 'Individual' },
      corporate: { bg: 'bg-warning-100', text: 'text-warning-700', label: 'Corporate' }
    };
    
    const config = typeConfig[type] || typeConfig.individual;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="bg-surface rounded-lg shadow-card overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-secondary-50 border-b border-border">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedClients.length === clients.length && clients.length > 0}
                  onChange={() => {
                    if (selectedClients.length === clients.length) {
                      clients.forEach(client => onClientSelect(client.id));
                    } else {
                      clients.forEach(client => {
                        if (!selectedClients.includes(client.id)) {
                          onClientSelect(client.id);
                        }
                      });
                    }
                  }}
                  className="rounded border-border focus:ring-primary"
                />
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-secondary-100 transition-smooth"
                onClick={() => onSort('name')}
              >
                <div className="flex items-center gap-2">
                  Client
                  {getSortIcon('name')}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Type & Location
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-secondary-100 transition-smooth"
                onClick={() => onSort('activeCases')}
              >
                <div className="flex items-center gap-2">
                  Active Cases
                  {getSortIcon('activeCases')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-secondary-100 transition-smooth"
                onClick={() => onSort('lastContact')}
              >
                <div className="flex items-center gap-2">
                  Last Contact
                  {getSortIcon('lastContact')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-secondary-100 transition-smooth"
                onClick={() => onSort('totalBillings')}
              >
                <div className="flex items-center gap-2">
                  Total Billings
                  {getSortIcon('totalBillings')}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-surface divide-y divide-border">
            {clients.map((client) => (
              <tr key={client.id} className="hover:bg-secondary-50 transition-smooth">
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedClients.includes(client.id)}
                    onChange={() => onClientSelect(client.id)}
                    className="rounded border-border focus:ring-primary"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-secondary-200 flex-shrink-0">
                      <Image
                        src={client.avatar}
                        alt={client.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="font-medium text-text-primary">{client.name}</div>
                      {client.businessRegNumber && (
                        <div className="text-sm text-text-secondary">Reg: {client.businessRegNumber}</div>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        {client.portalAccess && (
                          <Icon name="Globe" size={12} className="text-success-600" title="Portal Access" />
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm">
                    <div className="text-text-primary">{client.email}</div>
                    <div className="text-text-secondary">{client.phone}</div>
                    {client.mpesaNumber && (
                      <div className="text-success-600 text-xs">M-Pesa: {client.mpesaNumber}</div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="space-y-1">
                    {getClientTypeBadge(client.clientType)}
                    <div className="text-sm text-text-secondary">{client.county}</div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold text-text-primary">{client.activeCases}</span>
                    {client.activeCases > 0 && (
                      <Icon name="Briefcase" size={16} className="text-primary" />
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-text-primary">{formatDate(client.lastContact)}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="font-medium text-text-primary">{formatCurrency(client.totalBillings)}</div>
                </td>
                <td className="px-6 py-4">
                  {getStatusBadge(client.relationshipStatus)}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onClientAction('view', client)}
                      className="p-2 text-text-secondary hover:text-primary hover:bg-primary-50 rounded-lg transition-smooth"
                      title="View Details"
                    >
                      <Icon name="Eye" size={16} />
                    </button>
                    <button
                      onClick={() => onClientAction('call', client)}
                      className="p-2 text-text-secondary hover:text-success-600 hover:bg-success-50 rounded-lg transition-smooth"
                      title="Call Client"
                    >
                      <Icon name="Phone" size={16} />
                    </button>
                    <button
                      onClick={() => onClientAction('email', client)}
                      className="p-2 text-text-secondary hover:text-warning-600 hover:bg-warning-50 rounded-lg transition-smooth"
                      title="Send Email"
                    >
                      <Icon name="Mail" size={16} />
                    </button>
                    <button
                      onClick={() => onClientAction('sms', client)}
                      className="p-2 text-text-secondary hover:text-primary hover:bg-primary-50 rounded-lg transition-smooth"
                      title="Send SMS"
                    >
                      <Icon name="MessageSquare" size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4 p-4">
        {clients.map((client) => (
          <div key={client.id} className="bg-surface border border-border rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={selectedClients.includes(client.id)}
                  onChange={() => onClientSelect(client.id)}
                  className="rounded border-border focus:ring-primary mt-1"
                />
                <div className="w-12 h-12 rounded-full overflow-hidden bg-secondary-200 flex-shrink-0">
                  <Image
                    src={client.avatar}
                    alt={client.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="font-medium text-text-primary">{client.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    {getClientTypeBadge(client.clientType)}
                    {client.portalAccess && (
                      <Icon name="Globe" size={12} className="text-success-600" />
                    )}
                  </div>
                </div>
              </div>
              {getStatusBadge(client.relationshipStatus)}
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
              <div>
                <div className="text-text-secondary">Contact</div>
                <div className="text-text-primary">{client.phone}</div>
                <div className="text-text-secondary text-xs">{client.email}</div>
              </div>
              <div>
                <div className="text-text-secondary">Location</div>
                <div className="text-text-primary">{client.county}</div>
              </div>
              <div>
                <div className="text-text-secondary">Active Cases</div>
                <div className="text-text-primary font-medium">{client.activeCases}</div>
              </div>
              <div>
                <div className="text-text-secondary">Total Billings</div>
                <div className="text-text-primary font-medium">{formatCurrency(client.totalBillings)}</div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-border">
              <div className="text-sm text-text-secondary">
                Last contact: {formatDate(client.lastContact)}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onClientAction('call', client)}
                  className="p-2 text-success-600 hover:bg-success-50 rounded-lg transition-smooth"
                  title="Call"
                >
                  <Icon name="Phone" size={18} />
                </button>
                <button
                  onClick={() => onClientAction('email', client)}
                  className="p-2 text-warning-600 hover:bg-warning-50 rounded-lg transition-smooth"
                  title="Email"
                >
                  <Icon name="Mail" size={18} />
                </button>
                <button
                  onClick={() => onClientAction('view', client)}
                  className="p-2 text-primary hover:bg-primary-50 rounded-lg transition-smooth"
                  title="View"
                >
                  <Icon name="Eye" size={18} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {clients.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Users" size={48} className="mx-auto text-text-secondary mb-4" />
          <h3 className="text-lg font-medium text-text-primary mb-2">No clients found</h3>
          <p className="text-text-secondary">Try adjusting your search criteria or add a new client.</p>
        </div>
      )}
    </div>
  );
};

export default ClientTable;